/**
 * The return reference, kept so a shopper who comes back is not asked for something they
 * were never given a way to write down (SAI-1325).
 *
 * Same scope discipline as `./shopper.ts`, and for the same reasons:
 *   - `sessionStorage`, never `localStorage`. It dies with the tab, so a shared phone does
 *     not hand the next person a way back in.
 *   - The reference ONLY. Not the phone, not a line, not a price. Tracking needs the
 *     reference AND the phone, so a reference on its own opens nothing: it is the half of
 *     the pair that is safe to keep, which is exactly why this stores only that half.
 *   - Keyed by merchant slug, so two stores open at once do not offer each other's.
 */

export interface RememberedReference {
  merchantSlug: string;
  reference: string;
}

const SESSION_REFERENCE_KEY = "sirdab.returns.reference";

function storageOf(storage?: Storage | undefined): Storage | undefined {
  return storage ?? globalThis.sessionStorage;
}

function parse(raw: unknown): RememberedReference | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null) return null;
  const candidate = value as Partial<RememberedReference>;
  if (
    typeof candidate.merchantSlug !== "string" ||
    candidate.merchantSlug.trim() === "" ||
    typeof candidate.reference !== "string" ||
    candidate.reference.trim() === ""
  ) {
    return null;
  }
  return {
    merchantSlug: candidate.merchantSlug,
    reference: candidate.reference,
  };
}

/** The reference last seen for this merchant in this tab, or null. */
export function readRememberedReference(
  merchantSlug: string,
  storage?: Storage | undefined,
): string | null {
  const store = storageOf(storage);
  if (store === undefined) return null;
  let raw: string | null;
  try {
    raw = store.getItem(SESSION_REFERENCE_KEY);
  } catch {
    // Private mode, blocked site data. Not an error worth showing a shopper mid-return.
    return null;
  }
  const parsed = parse(raw);
  if (parsed === null || parsed.merchantSlug !== merchantSlug) return null;
  return parsed.reference;
}

export function writeRememberedReference(
  value: RememberedReference,
  storage?: Storage | undefined,
): void {
  const store = storageOf(storage);
  if (store === undefined) return;
  try {
    store.setItem(SESSION_REFERENCE_KEY, JSON.stringify(value));
  } catch {
    // Same swallow as the read: keeping this is a convenience, never a step in the flow.
  }
}

export function clearRememberedReference(storage?: Storage | undefined): void {
  const store = storageOf(storage);
  if (store === undefined) return;
  try {
    store.removeItem(SESSION_REFERENCE_KEY);
  } catch {
    // Ignored, as above.
  }
}

/**
 * The link a shopper can bookmark to come back to this return.
 *
 * The reference rides in the query string and the phone never does. That pairing is the
 * point: the link alone opens nothing, so it is safe in a bookmark bar, a note, or a
 * message to themselves, and all it saves them is the part they cannot remember.
 */
export function trackingLink(trackPath: string, reference: string): string {
  return `${trackPath}?ref=${encodeURIComponent(reference)}`;
}

/** The reference a bookmarked link carries, if it carries a plausible one. */
export function referenceFromSearch(search: string): string | null {
  let value: string | null;
  try {
    value = new URLSearchParams(search).get("ref");
  } catch {
    return null;
  }
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}
